/**
 * AWS AppConfig Feature Flags Provider
 */

import React, { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';
import { FeatureFlagsContextType, FeatureFlags } from '../types/appConfig';
import appConfigService from '../services/appConfigService';
import env from '../env';

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

interface AppConfigProviderProps {
  children: ReactNode;
  enablePolling?: boolean;
  pollInterval?: number;
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({
  children,
  enablePolling = true,
  pollInterval,
}) => {
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  const refreshFlags = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const fetchedFlags = await appConfigService.fetchFlags();
      setFlags(fetchedFlags);
      setInitialized(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  const isFeatureEnabled = useCallback((flagName: string): boolean => {
    return flags[flagName]?.enabled ?? false;
  }, [flags]);

  useEffect(() => {
    if (!env.APP_CONFIG_ENABLED) {
      setLoading(false);
      setInitialized(true);
      return;
    }

    refreshFlags();
  }, [refreshFlags]);

  useEffect(() => {
    if (!env.APP_CONFIG_ENABLED) return;

    const unsubscribe = appConfigService.subscribe((newFlags) => {
      setFlags(newFlags);
    });

    if (enablePolling && initialized) {
      appConfigService.startPolling(pollInterval);
    }

    return () => {
      unsubscribe();
      if (enablePolling) {
        appConfigService.stopPolling();
      }
    };
  }, [enablePolling, pollInterval, initialized]);

  const contextValue: FeatureFlagsContextType = {
    flags,
    loading,
    error,
    initialized,
    refreshFlags,
    isFeatureEnabled,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags context
 */
export function useAppConfig(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
}

/**
 * Hook to check if a feature flag is enabled
 */
export function useFeatureFlag(flagName: string, defaultValue: boolean = false): boolean {
  const { flags, initialized } = useAppConfig();

  if (!initialized) {
    return defaultValue;
  }

  return flags[flagName]?.enabled ?? defaultValue;
}

export { FeatureFlagsContext };
export default AppConfigProvider;
